import React, { useContext } from 'react';
import { DownOutlined, SvgIcon, Tooltip, UpOutlined, VirtualTable } from '@fx-ui/fine-design';
import COLOR from '@fx-ui/color';
import { assign, cloneDeep, concat, find, get, isEmpty, isObjectEmpty, map } from '@libs/shared';
import { CustomCombo } from '../../../../../components';
import {
    BaseArgumentValue,
    IFunctionArgumentFrom,
    PLUGIN_ARGUMENT_VALUE_FROM,
    PluginArgumentValue,
    VectorArgumentField
} from '../../../../../interfaces';
import PluginComponent, { PluginComponentContext } from '../../../plugin-component';
import ExpandIcon from '../../../../../assets/svgicons/expand.svg';
import TrashIcon from '../../../../../assets/svgicons/trash.svg';
import { Intl } from '../../../../../constants';

const ResolverTypeSelect: React.FC<{
    resolverTypeList: IFunctionArgumentFrom[];
    fieldType: string;
    isEnabled?: boolean;
    isView?: boolean;
    value?: PLUGIN_ARGUMENT_VALUE_FROM;
    onChange: (v: PLUGIN_ARGUMENT_VALUE_FROM) => void;
}> = (props) => {
    const { resolverTypeList, fieldType, isEnabled = true, value, isView, onChange } = props;

    const comboItems = map(
        get(
            find(resolverTypeList, (v) => {
                return v.field_type === fieldType;
            }),
            'resolver_types',
            []
        ),
        (value) => {
            const item = { value };
            if (value === PLUGIN_ARGUMENT_VALUE_FROM.LITERAL) {
                return assign(item, {
                    text: Intl.get('plugin.value.type.literal')
                });
            }
            if (value === PLUGIN_ARGUMENT_VALUE_FROM.PROCESS_VAR) {
                return assign(item, {
                    text: Intl.get('plugin.value.type.process_var')
                });
            }
            if (value === PLUGIN_ARGUMENT_VALUE_FROM.EXPR) {
                return assign(item, {
                    text: Intl.get('plugin.value.type.expr')
                });
            }
            return assign(item, {
                text: ''
            });
        }
    );
    return (
        <CustomCombo
            isView={isView}
            value={value}
            onValueChange={onChange}
            hasSearch={false}
            className="resolver-select-combo"
            items={comboItems}
            disabled={!isEnabled}
        >
            {(_a, _b, active) => {
                return active ? (
                    <UpOutlined className="icon-btn" />
                ) : (
                    <DownOutlined className="icon-btn" />
                );
            }}
        </CustomCombo>
    );
};

interface MapVectorProps {
    subform: VectorArgumentField;
    value?: BaseArgumentValue[][];
    resolverTypeList: IFunctionArgumentFrom[];
    onRowExpand: (rowIndex: number) => void;
}

const MapVector: React.FC<MapVectorProps> = (props) => {
    const { subform, value, resolverTypeList, onRowExpand } = props;

    const pluginComponentStore = useContext(PluginComponentContext);

    if (!pluginComponentStore || isEmpty(value)) {
        return null;
    }

    const { isView, checkTipVisible, onChange } = pluginComponentStore;

    const subFields = subform.fieldConf?.fields;
    const firstRowValue = value![0];

    const getColumns = () => {
        return concat<any>(
            isView
                ? []
                : [
                      {
                          title: '',
                          width: 78,
                          fixed: true,
                          dataIndex: 'tools',
                          render: (_c: unknown, _r: unknown, rowNum: number) => {
                              return (
                                  <div className="tools-btn">
                                      <SvgIcon
                                          component={ExpandIcon}
                                          onClick={() => {
                                              onRowExpand(rowNum);
                                          }}
                                          className="expand-btn"
                                      />
                                      <SvgIcon
                                          component={TrashIcon}
                                          onClick={() => {
                                              const temp = [...value!];
                                              temp.splice(rowNum, 1);
                                              onChange(temp);
                                          }}
                                          className="del-btn"
                                      />
                                  </div>
                              );
                          }
                      }
                  ],
            map(subFields, (field, colIndex) => {
                const { label, isRequired, fieldType, isEnabled } = field;
                const resolverType =
                    firstRowValue?.[colIndex]?.resolver_type || PLUGIN_ARGUMENT_VALUE_FROM.LITERAL;

                return {
                    key: field.id,
                    width: 160,
                    dataIndex: field.id,
                    title: (
                        <div className="head-cell">
                            <div className="content" title={label}>
                                {isRequired && <span className="required">*</span>}
                                {label}
                            </div>
                            <ResolverTypeSelect
                                value={resolverType}
                                fieldType={fieldType}
                                isEnabled={isEnabled}
                                isView={isView}
                                resolverTypeList={resolverTypeList}
                                onChange={(v) => {
                                    let emptyValue: any;
                                    switch (v) {
                                        case PLUGIN_ARGUMENT_VALUE_FROM.EXPR:
                                        case PLUGIN_ARGUMENT_VALUE_FROM.LITERAL:
                                            emptyValue = '';
                                            break;
                                        case PLUGIN_ARGUMENT_VALUE_FROM.PROCESS_VAR:
                                            emptyValue = null;
                                            break;
                                    }
                                    onChange(
                                        map(value, (item) => {
                                            const draftItem = [...item];
                                            draftItem[colIndex] = {
                                                ...draftItem[colIndex],
                                                resolver_type: v,
                                                resolver_arg: emptyValue
                                            } as any as BaseArgumentValue;
                                            return draftItem;
                                        })
                                    );
                                }}
                            />
                        </div>
                    ),

                    render: (_: unknown, rowData: PluginArgumentValue[], rowIndex: number) => {
                        const cellValue = rowData[colIndex]!;

                        const renderPluginComponent = () => {
                            return (
                                <PluginComponent
                                    {...pluginComponentStore}
                                    field={field}
                                    hideLabel
                                    hideResolverTypeSelect
                                    fieldValue={cellValue}
                                    onChange={(v) => {
                                        const temp = cloneDeep(value!);
                                        temp[rowIndex]![colIndex] = {
                                            ...temp[rowIndex]![colIndex],
                                            ...v
                                        } as any as BaseArgumentValue;
                                        onChange(temp);
                                    }}
                                />
                            );
                        };

                        const showErrorTip =
                            checkTipVisible &&
                            field.isRequired &&
                            isObjectEmpty(cellValue.resolver_arg);

                        return showErrorTip ? (
                            <>
                                <div className="error-tip" />
                                <Tooltip
                                    arrow={false}
                                    content={
                                        <div
                                            style={{
                                                color: COLOR.red6,
                                                background: COLOR.red1,
                                                padding: '3px 4px'
                                            }}
                                        >
                                            {Intl.get('item.required')}
                                        </div>
                                    }
                                    hasPadding={false}
                                    placement="top-left"
                                    effect="light"
                                >
                                    <div className="field-cell">{renderPluginComponent()}</div>
                                </Tooltip>
                            </>
                        ) : (
                            renderPluginComponent()
                        );
                    }
                };
            })
        );
    };

    return (
        <div className="fx-map-vector">
            <VirtualTable
                className="subform-table"
                primaryKey="id"
                dataSource={value!}
                columns={getColumns()}
                hasCellBorder
                blankAllowScroll={false}
            />
        </div>
    );
};

export default MapVector;
